import React from 'react';
import DNCTooltip from '../DNCTooltip';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from "react-redux";
import { openClickToDial } from "data/user/actions";

import {
  selectIsClickToDialLoaded,
} from "data/user/selectors";

import classNames from 'classnames';

import css from './style.scss';

import { isPropStream } from '../../../utils/brand';

// This component is to be used in AG Grid cellRenderer for rendering contact information with DNC Tooltip
// Supports
// 1. New line format where each number is in new line
// 2. Inline format where multiple numbers are separated by comma
const ContactCellRenderer = (props) => {
  const { value, inLine, data, viewMode } = props;
  const dispatch = useDispatch();

  const isClickToDialLoaded = useSelector(selectIsClickToDialLoaded);

  // Only allow click-to-dial for PropStream accounts
  const canUseDial = isClickToDialLoaded && isPropStream();

  const convertPhoneNumberTextToNumbers = (phoneText) => {
    return phoneText ? phoneText.split("").filter((character) => !Number.isNaN(parseInt(character))).join("") : null;
  }

  if (value && Array.isArray(value)) {
    return <div className={classNames(css.clsBaseLine, { [css.clsInLine]: !!inLine })}>
      {value.map((eachNumber, numberIndex) => {
        if (!eachNumber.number.trim()) return null;
        const isDnc = eachNumber.myDnc || eachNumber.publicDnc;
        return <div key={numberIndex} style={{ marginBottom: ((numberIndex < value.length - 1) && !inLine) ? '4px' : '0' }}>
          <p
            className={canUseDial && !viewMode ? css.clsPhoneNumberText : null}
            style={inLine && { width: isDnc ? "135px" : "105px" }}
          >
            <span
              style={canUseDial && !viewMode ? { cursor: "pointer" } : null}
              onClick={() => {
                if (canUseDial && !viewMode) {
                  dispatch(
                    openClickToDial({
                      contactId: eachNumber?.contactId ? eachNumber?.contactId : data?.id,
                      phonenumber: convertPhoneNumberTextToNumbers(eachNumber.number),
                      score: eachNumber.score,
                      myDnc: eachNumber.myDnc,
                      publicDnc: eachNumber.publicDnc,
                      pab: eachNumber.pab,
                      litigatorFlag: data?.litigatorFlag,
                    })
                  );
                }
              }}
            >
              {eachNumber.number}
            </span>

            {isDnc && !viewMode && (
              <DNCTooltip publicDnc={eachNumber.publicDnc} myDnc={eachNumber.myDnc}>
                <span style={{ color: "#CA1551", marginLeft: "2px", border: "1px solid #CA1551", borderRadius: "999px", padding: "0px 4px", fontSize: "10px", fontWeight: 600 }}>DNC</span>
              </DNCTooltip>
            )}
            {isDnc && viewMode && (
                <span style={{ color: "#CA1551", marginLeft: "2px", border: "1px solid #CA1551", borderRadius: "999px", padding: "0px 4px", fontSize: "10px", fontWeight: 600 }}>DNC</span>
            )}

            {numberIndex < value.length - 1 && inLine && <span>,</span>}
          </p>
        </div>
      })}
    </div>
  }

  if (!value || typeof value !== 'string') {
    return <div>{value}</div>;
  }

  const lines = value.split('\n');

  return (
    <div className="clsBaseLine">
      {lines.map((line, lineIndex) => {
        if (!line.trim()) return null;

        const parts = line.split(/(\(DNC\))/gi);

        return (
          <div key={lineIndex} style={{ marginBottom: lineIndex < lines.length - 1 ? '4px' : '0' }}>
            {parts.map((part, partIndex) => {
              const isDNC = /^\(DNC\)$/i.test(part);
              return isDNC ? (
                viewMode ? (
                  // view mode: plain red text, no tooltip
                  <span key={partIndex} style={{ color: "#CA1551" }}>
                    {part}
                  </span>
                ) : (
                  <DNCTooltip key={partIndex} publicDnc myDnc>
                    <span style={{ color: "#CA1551", cursor: "pointer" }}>
                      {part}
                    </span>
                  </DNCTooltip>
                )
              ) : (
                <span key={partIndex}>{part}</span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};



ContactCellRenderer.propTypes = {
  /** The cell value to render */
  value: PropTypes.arrayOf(PropTypes.shape({
    /** Phone number  */
    number: PropTypes.string.isRequired,
    score: PropTypes.number,
    myDnc: PropTypes.bool,
    publicDnc: PropTypes.bool,
    pab: PropTypes.bool,
  })),
  /** Renders the contact in same line */
  inLine: PropTypes.bool,
  viewMode: PropTypes.bool,
};

ContactCellRenderer.defaultProps = {
  viewMode: false,
};

export default React.memo(ContactCellRenderer);
